import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip as AntTooltip, Button, Checkbox } from 'antd';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { UnorderedListOutlined, BarChartOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { PERIOD1_COLOR, PERIOD2_COLOR, PERCENT_THRESHOLD, MAX_SLICES } from '../constants/comparisonConstants';
import { isValidArray } from '../utils/comparisonUtils';
import PeriodDot from '../components/PeriodDot';
import { legendItemStyle, legendSeparatorStyle, tooltipStyle, tooltipTitleStyle, tooltipValueStyle } from '../styles/comparisonStyles';

const BarChartComparison = memo(({ data = [], data2 = [], categories = [], categories2 = [], onClick, activeFilters = [], disabled = false, columnsCount = 3, onRenderHeaderButton, renderMode = 'both', externalHoveredCategory = null, externalSetHoveredCategory = null, externalActiveCategories = null, externalSetActiveCategories = null, onOthersClick }) => {
    const { t } = useTranslation();
    const isFourColumns = columnsCount >= 4;

    // Combina dados de ambos os períodos
    const combinedData = useMemo(() => {
        const categoryMap = new Map();

        // Adiciona dados do período 1
        data.forEach((value, index) => {
            const category = categories[index];
            if (category) {
                categoryMap.set(category, {
                    name: category,
                    valuePeriod1: value || 0,
                    valuePeriod2: 0
                });
            }
        });

        // Adiciona dados do período 2
        data2.forEach((value, index) => {
            const category = categories2[index];
            if (category) {
                const existing = categoryMap.get(category);
                if (existing) {
                    existing.valuePeriod2 = value || 0;
                } else {
                    categoryMap.set(category, {
                        name: category,
                        valuePeriod1: 0,
                        valuePeriod2: value || 0
                    });
                }
            }
        });

        return Array.from(categoryMap.values());
    }, [data, data2, categories, categories2]);

    // Calcula totais
    const totalPeriod1 = useMemo(() => combinedData.reduce((sum, item) => sum + item.valuePeriod1, 0), [combinedData]);
    const totalPeriod2 = useMemo(() => combinedData.reduce((sum, item) => sum + item.valuePeriod2, 0), [combinedData]);
    const totalCombined = totalPeriod1 + totalPeriod2;

    // Prepara dados originais com percentuais
    const originalData = useMemo(() => {
        return combinedData.map(item => ({
            ...item,
            totalValue: item.valuePeriod1 + item.valuePeriod2,
            percentPeriod1: totalPeriod1 > 0 ? (item.valuePeriod1 / totalPeriod1) * 100 : 0,
            percentPeriod2: totalPeriod2 > 0 ? (item.valuePeriod2 / totalPeriod2) * 100 : 0,
            percentCombined: totalCombined > 0 ? ((item.valuePeriod1 + item.valuePeriod2) / totalCombined) * 100 : 0
        }));
    }, [combinedData, totalPeriod1, totalPeriod2, totalCombined]);

    // Ordena por valor total combinado
    const sortedListData = useMemo(() => {
        return [...originalData].sort((a, b) => b.totalValue - a.totalValue);
    }, [originalData]);

    // Validação de props
    if (!isValidArray(data) && !isValidArray(data2)) {
        return null;
    }

    // Lógica de agrupamento OTHERS
    // Aplica agrupamento apenas quando houver mais de 9 itens;
    // caso contrário, todas as categorias aparecem individualmente.
    const totalItems = sortedListData.length;

    let smallSlices = [];
    let bigSlices = [];
    let visibleBig = [];
    let overflowBig = [];
    let othersGroup = [];
    let othersValuePeriod1 = 0;
    let othersValuePeriod2 = 0;
    let othersTotal = 0;
    let othersPercent = 0;

    if (totalItems >= 9) {
        smallSlices = sortedListData.filter(i => i.percentCombined < PERCENT_THRESHOLD);
        bigSlices = sortedListData.filter(i => i.percentCombined >= PERCENT_THRESHOLD);
        visibleBig = bigSlices.slice(0, MAX_SLICES - 1);
        overflowBig = bigSlices.slice(MAX_SLICES - 1);

        othersGroup = [...smallSlices, ...overflowBig];
        othersValuePeriod1 = othersGroup.reduce((sum, i) => sum + i.valuePeriod1, 0);
        othersValuePeriod2 = othersGroup.reduce((sum, i) => sum + i.valuePeriod2, 0);
        othersTotal = othersValuePeriod1 + othersValuePeriod2;
        othersPercent = totalCombined > 0 ? (othersTotal / totalCombined) * 100 : 0;
    } else {
        bigSlices = [...sortedListData];
        visibleBig = [...sortedListData];
        overflowBig = [];
        othersGroup = [];
        othersValuePeriod1 = 0;
        othersValuePeriod2 = 0;
        othersTotal = 0;
        othersPercent = 0;
    }

    // Monta dados do gráfico combinando ambos os períodos
    const chartData = useMemo(() => {
        const result = [];
        
        // Adiciona categorias visíveis
        visibleBig.forEach(item => {
            result.push({
                name: item.name,
                categoryName: item.name,
                valuePeriod1: item.valuePeriod1,
                valuePeriod2: item.valuePeriod2,
                percentPeriod1: item.percentPeriod1,
                percentPeriod2: item.percentPeriod2,
                displayName: item.name
            });
        });

        // Adiciona OTHERS se houver
        if (othersGroup.length > 0 && othersTotal > 0) {
            result.push({
                name: "OTHERS",
                categoryName: "OTHERS",
                valuePeriod1: othersValuePeriod1,
                valuePeriod2: othersValuePeriod2,
                percentPeriod1: totalPeriod1 > 0 ? (othersValuePeriod1 / totalPeriod1) * 100 : 0,
                percentPeriod2: totalPeriod2 > 0 ? (othersValuePeriod2 / totalPeriod2) * 100 : 0,
                displayName: t("Dashboard.Others")
            });
        }

        return result;
    }, [visibleBig, othersGroup, othersValuePeriod1, othersValuePeriod2, othersTotal, totalPeriod1, totalPeriod2, t]);

    // Estados - usa estado externo se fornecido, senão usa estado interno
    const [internalActiveCategories, setInternalActiveCategories] = useState([]);
    const [internalHoveredCategory, setInternalHoveredCategory] = useState(null);
    const activeCategories = externalActiveCategories !== null ? externalActiveCategories : internalActiveCategories;
    const setActiveCategories = externalSetActiveCategories !== null ? externalSetActiveCategories : setInternalActiveCategories;
    const hoveredCategory = externalHoveredCategory !== null ? externalHoveredCategory : internalHoveredCategory;
    const setHoveredCategory = externalSetHoveredCategory !== null ? externalSetHoveredCategory : setInternalHoveredCategory;
    const [hasAnimated, setHasAnimated] = useState(false);
    const [showOthersItems, setShowOthersItems] = useState(false);
    const [showDetails, setShowDetails] = useState(!isFourColumns);

    useEffect(() => {
        if (externalActiveCategories === null && JSON.stringify(activeFilters) !== JSON.stringify(activeCategories)) {
            setActiveCategories(activeFilters);
        }
    }, [activeFilters, externalActiveCategories, activeCategories, setActiveCategories]);

    useEffect(() => {
        setShowDetails(!isFourColumns);
    }, [isFourColumns]);

    useEffect(() => {
        if (onRenderHeaderButton && isFourColumns) {
            onRenderHeaderButton(
                <Button
                    type="text"
                    icon={showDetails ? <BarChartOutlined /> : <UnorderedListOutlined />}
                    onClick={() => setShowDetails(!showDetails)}
                    style={{
                        color: showDetails ? '#4085c6' : '#8c8c8c',
                        padding: '4px 8px',
                        height: 'auto',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#4085c6';
                        e.currentTarget.style.backgroundColor = '#f0f7ff';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.color = showDetails ? '#4085c6' : '#8c8c8c';
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title={showDetails ? t('Dashboard.ShowChart') : t('Dashboard.Details')}
                />
            );
        } else if (onRenderHeaderButton) {
            onRenderHeaderButton(null);
        }
    }, [showDetails, isFourColumns, onRenderHeaderButton, t]);

    const toggleSelection = (category) => {
        if (disabled) return;
        if (category === "OTHERS") return;
        
        const isActive = activeCategories.includes(category);
        const newCategories = isActive 
            ? activeCategories.filter(c => c !== category) 
            : [...activeCategories, category];
        
        if (externalSetActiveCategories === null && onClick) {
            onClick(category);
        }
        
        setActiveCategories(newCategories);
    };

    const onBarClick = (data, index) => {
        if (index >= 0 && index < chartData.length) {
            const clickedItem = chartData[index];
            if (clickedItem && clickedItem.categoryName === "OTHERS") {
                setShowOthersItems(!showOthersItems);
                onOthersClick && onOthersClick();
            } else if (clickedItem && clickedItem.categoryName !== "OTHERS") {
                toggleSelection(clickedItem.categoryName);
            }
        }
    };

    const onListItemMouseEnter = (category) => {
        setHoveredCategory(category);
    };

    const onListItemMouseLeave = () => {
        setHoveredCategory(null);
    };

    const onListItemClick = (category, e) => {
        e.stopPropagation();
        if (category === "OTHERS") {
            setShowOthersItems(!showOthersItems);
            onOthersClick && onOthersClick();
        } else {
            toggleSelection(category);
        }
    };

    if ((!data.length && !data2.length) || (!categories.length && !categories2.length)) {
        return <p>{t('Dashboard.Not')}</p>;
    }

    // Lista de dados para exibição (agrupados por categoria)
    const listData = useMemo(() => {
        const result = [];
        visibleBig.forEach(item => {
            result.push({
                name: item.name,
                displayName: item.name,
                valuePeriod1: item.valuePeriod1,
                valuePeriod2: item.valuePeriod2,
                totalValue: item.totalValue,
                percentPeriod1: item.percentPeriod1,
                percentPeriod2: item.percentPeriod2,
                percentCombined: item.percentCombined
            });
        });
        if (othersGroup.length > 0) {
            result.push({
                name: "OTHERS",
                displayName: t("Dashboard.Others"),
                valuePeriod1: othersValuePeriod1,
                valuePeriod2: othersValuePeriod2,
                totalValue: othersTotal,
                percentPeriod1: totalPeriod1 > 0 ? (othersValuePeriod1 / totalPeriod1) * 100 : 0,
                percentPeriod2: totalPeriod2 > 0 ? (othersValuePeriod2 / totalPeriod2) * 100 : 0,
                percentCombined: othersPercent
            });
        }
        return result;
    }, [visibleBig, othersGroup, othersValuePeriod1, othersValuePeriod2, othersTotal, othersPercent, totalPeriod1, totalPeriod2, t]);

    const shouldShowCharts = renderMode === 'charts' || renderMode === 'both';
    const shouldShowDetails = renderMode === 'details' || renderMode === 'both';

    // Calcula dimensões responsivas
    const chartsContainerWidth = renderMode === 'charts' ? "100%" : (isFourColumns ? (showDetails ? "0%" : "100%") : "70%");
    const detailsWidth = renderMode === 'details' ? "100%" : (isFourColumns ? (showDetails ? "100%" : "0%") : "30%");
    const chartHeight = 250;
    const barSize = 35;
    const fontSize = 11;

    return (
        <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            flexWrap: 'nowrap', 
            gap: 12, 
            flexDirection: 'row',
            position: 'relative',
            minHeight: chartHeight,
            width: '100%'
        }}>
            {shouldShowCharts && (!isFourColumns || !showDetails) && (
                <div style={{
                    width: chartsContainerWidth,
                    transition: 'width 0.3s ease, opacity 0.3s ease',
                    opacity: isFourColumns && showDetails ? 0 : 1
                }}>
                    <ResponsiveContainer width="100%" height={chartHeight}>
                        <BarChart 
                            data={chartData} 
                            margin={{ 
                                top: 20, 
                                right: 30, 
                                left: 20, 
                                bottom: 40 
                            }}
                            barCategoryGap="20%"
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                            <XAxis 
                                dataKey="name" 
                                tick={{ fontSize, fill: '#595959' }} 
                                axisLine={{ stroke: '#d9d9d9' }}
                                tickLine={{ stroke: '#d9d9d9' }}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                            />
                            <YAxis 
                                tick={{ fontSize, fill: '#595959' }}
                                axisLine={{ stroke: '#d9d9d9' }}
                                tickLine={{ stroke: '#d9d9d9' }}
                                width={50}
                            />
                            <Tooltip 
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const data = payload[0].payload;
                                        return (
                                            <div style={tooltipStyle}>
                                                <p style={tooltipTitleStyle}>
                                                    {data.displayName}
                                                </p>
                                                <p style={{ margin: '0 0 4px 0', color: '#595959', fontSize: '13px' }}>
                                                    <PeriodDot period={1} size={8} />
                                                    <span style={{ marginLeft: '4px' }}>Período 1: <strong style={{ color: '#262626' }}>{data.valuePeriod1}</strong></span>
                                                </p>
                                                <p style={{ margin: '0 0 8px 0', color: '#595959', fontSize: '13px' }}>
                                                    {t('Dashboard.Tooltip.Percentage')}: <strong style={{ color: '#4085c6' }}>{data.percentPeriod1.toFixed(2)}%</strong>
                                                </p>
                                                <p style={{ margin: '0 0 4px 0', color: '#595959', fontSize: '13px' }}>
                                                    <PeriodDot period={2} size={8} />
                                                    <span style={{ marginLeft: '4px' }}>Período 2: <strong style={{ color: '#262626' }}>{data.valuePeriod2}</strong></span>
                                                </p>
                                                <p style={{ margin: 0, color: '#595959', fontSize: '13px' }}>
                                                    {t('Dashboard.Tooltip.Percentage')}: <strong style={{ color: '#4085c6' }}>{data.percentPeriod2.toFixed(2)}%</strong>
                                                </p>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Bar
                                dataKey="valuePeriod1"
                                name="Período 1"
                                fill={PERIOD1_COLOR}
                                barSize={barSize}
                                radius={[4, 4, 0, 0]}
                                onClick={onBarClick}
                                onMouseMove={(data, index, e) => {
                                    if (data && data.payload) {
                                        const category = data.payload.categoryName;
                                        if (category !== hoveredCategory) {
                                            onListItemMouseEnter(category);
                                        }
                                    }
                                }}
                                onMouseLeave={onListItemMouseLeave}
                                cursor={disabled ? "not-allowed" : "pointer"}
                                isAnimationActive={!hasAnimated}
                                animationBegin={0}
                                animationDuration={800}
                                animationEasing="ease-out"
                                onAnimationEnd={() => setHasAnimated(true)}
                            >
                                {chartData.map((entry, index) => {
                                    const opacity = hoveredCategory
                                        ? (entry.categoryName === hoveredCategory ? 1 : 0.3)
                                        : (activeCategories.length === 0 || activeCategories.includes(entry.categoryName) ? 1 : 0.3);
                                    const isHovered = hoveredCategory === entry.categoryName;
                                    const isActive = activeCategories.length === 0 || activeCategories.includes(entry.categoryName);
                                    
                                    return (
                                        <Cell
                                            key={`cell-p1-${index}`}
                                            fill={PERIOD1_COLOR}
                                            opacity={opacity}
                                            stroke={isHovered || isActive ? '#fff' : 'transparent'}
                                            strokeWidth={isHovered || isActive ? 2 : 0}
                                        />
                                    );
                                })}
                            </Bar>
                            <Bar
                                dataKey="valuePeriod2"
                                name="Período 2"
                                fill={PERIOD2_COLOR}
                                barSize={barSize}
                                radius={[4, 4, 0, 0]}
                                onClick={onBarClick}
                                onMouseMove={(data, index, e) => {
                                    if (data && data.payload) {
                                        const category = data.payload.categoryName;
                                        if (category !== hoveredCategory) {
                                            onListItemMouseEnter(category);
                                        }
                                    }
                                }}
                                onMouseLeave={onListItemMouseLeave}
                                cursor={disabled ? "not-allowed" : "pointer"}
                                isAnimationActive={!hasAnimated}
                                animationBegin={0}
                                animationDuration={800}
                                animationEasing="ease-out"
                                onAnimationEnd={() => setHasAnimated(true)}
                            >
                                {chartData.map((entry, index) => {
                                    const opacity = hoveredCategory
                                        ? (entry.categoryName === hoveredCategory ? 1 : 0.3)
                                        : (activeCategories.length === 0 || activeCategories.includes(entry.categoryName) ? 1 : 0.3);
                                    const isHovered = hoveredCategory === entry.categoryName;
                                    const isActive = activeCategories.length === 0 || activeCategories.includes(entry.categoryName);
                                    
                                    return (
                                        <Cell
                                            key={`cell-p2-${index}`}
                                            fill={PERIOD2_COLOR}
                                            opacity={opacity}
                                            stroke={isHovered || isActive ? '#fff' : 'transparent'}
                                            strokeWidth={isHovered || isActive ? 2 : 0}
                                        />
                                    );
                                })}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {shouldShowDetails && (!isFourColumns || showDetails) && (
                <div style={{ 
                    maxHeight: 300, 
                    overflowY: 'auto', 
                    width: renderMode === 'details' ? '100%' : detailsWidth, 
                    paddingLeft: 8, 
                    paddingRight: 0,
                    transition: 'width 0.3s ease, opacity 0.3s ease',
                    opacity: isFourColumns && !showDetails ? 0 : 1,
                    animation: isFourColumns && showDetails ? 'fadeIn 0.3s ease' : 'none'
                }}>
                    <h4 style={{ marginBottom: 4, fontSize: 14, fontWeight: 600, color: '#262626' }}>{t('Dashboard.Details')}</h4>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {listData.map((entry, index) => {
                            const isOthers = entry.name === "OTHERS";
                            const isOthersExpanded = isOthers && showOthersItems;
                            
                            return (
                                <Fragment key={`list-item-${index}`}>
                                    <AntTooltip title={`${entry.displayName}: P1: ${entry.valuePeriod1} (${entry.percentPeriod1.toFixed(2)}%) | P2: ${entry.valuePeriod2} (${entry.percentPeriod2.toFixed(2)}%)`}>
                                        <li
                                            onMouseEnter={() => !isOthers && onListItemMouseEnter(entry.name)}
                                            onMouseLeave={onListItemMouseLeave}
                                            onClick={(e) => onListItemClick(entry.name, e)}
                                            style={{
                                                padding: '3px 4px',
                                                marginBottom: 1,
                                                cursor: 'pointer',
                                                backgroundColor: activeCategories.includes(entry.name) ? 'rgba(64, 133, 198, 0.08)' : 'transparent',
                                                borderRadius: '3px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                flexWrap: 'nowrap',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                fontSize: 13,
                                                transition: 'all 0.2s ease',
                                                border: activeCategories.includes(entry.name) ? '1px solid #4085c6' : '1px solid transparent',
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                                {isOthers && (
                                                    <span style={{ marginRight: '4px', fontSize: '10px' }}>
                                                        {isOthersExpanded ? <UpOutlined /> : <DownOutlined />}
                                                    </span>
                                                )}
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {entry.displayName}
                                                </span>
                                            </div>
                                            <strong style={{ ...legendItemStyle, marginLeft: 3, flexShrink: 0, fontSize: 12 }}>
                                                <span style={legendItemStyle}>
                                                    <PeriodDot period={1} size={8} />
                                                    P1: {entry.valuePeriod1} ({entry.percentPeriod1.toFixed(2)}%)
                                                </span>
                                                <span style={legendSeparatorStyle}>|</span>
                                                <span style={legendItemStyle}>
                                                    <PeriodDot period={2} size={8} />
                                                    P2: {entry.valuePeriod2} ({entry.percentPeriod2.toFixed(2)}%)
                                                </span>
                                            </strong>
                                        </li>
                                    </AntTooltip>
                                    
                                    {isOthers && isOthersExpanded && othersGroup.length > 0 && (
                                        <li style={{ paddingLeft: '20px', marginTop: '4px', marginBottom: '4px' }}>
                                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                {othersGroup.map((item, itemIndex) => {
                                                    const itemPercentPeriod1 = totalPeriod1 > 0 ? (item.valuePeriod1 / totalPeriod1) * 100 : 0;
                                                    const itemPercentPeriod2 = totalPeriod2 > 0 ? (item.valuePeriod2 / totalPeriod2) * 100 : 0;
                                                    const isItemActive = activeCategories.includes(item.name);
                                                    return (
                                                        <li
                                                            key={`others-item-${itemIndex}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleSelection(item.name);
                                                            }}
                                                            onMouseEnter={() => onListItemMouseEnter(item.name)}
                                                            onMouseLeave={onListItemMouseLeave}
                                                            style={{
                                                                padding: '3px 4px',
                                                                marginBottom: 1,
                                                                cursor: 'pointer',
                                                                backgroundColor: isItemActive ? 'rgba(64, 133, 198, 0.08)' : 'transparent',
                                                                borderRadius: '3px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                fontSize: 12,
                                                                transition: 'all 0.2s ease',
                                                                border: isItemActive ? '1px solid #4085c6' : '1px solid transparent',
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                                                <Checkbox
                                                                    checked={isItemActive}
                                                                    onChange={() => toggleSelection(item.name)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    style={{ marginRight: '6px' }}
                                                                />
                                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {item.name}
                                                                </span>
                                                            </div>
                                                            <strong style={{ marginLeft: 3, color: '#595959', flexShrink: 0, fontSize: 11, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                <span style={legendItemStyle}>
                                                                    <PeriodDot period={1} size={8} />
                                                                    P1: {item.valuePeriod1} ({itemPercentPeriod1.toFixed(2)}%)
                                                                </span>
                                                                <span style={legendSeparatorStyle}>|</span>
                                                                <span style={legendItemStyle}>
                                                                    <PeriodDot period={2} size={8} />
                                                                    P2: {item.valuePeriod2} ({itemPercentPeriod2.toFixed(2)}%)
                                                                </span>
                                                            </strong>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </li>
                                    )}
                                </Fragment>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
});

BarChartComparison.displayName = 'BarChartComparison';

export default BarChartComparison;
